import { usePress } from "@react-aria/interactions";
import { Box, Columns, Column, Children, IconProps, Label } from "..";
import { LocalizedString } from "../util/LocalizedString";
import { tabRecipe } from "./Tabs.css";
import { normalizeStatusValue } from "../internal/sprinkles.css";
import { useBentoConfig } from "../BentoConfigContext";

export type TabsSize = "medium" | "large";

type Props<A> = {
  size: TabsSize;
  value: A;
  onChange: (v: A) => void;
  tabs: Array<{
    value: A;
    label: LocalizedString;
    disabled?: boolean;
    icon?: (props: IconProps) => Children;
    hasNotification?: boolean;
    rightAccessory?: Children;
  }>;
};

type TabProps = {
  size: TabsSize;
  label: LocalizedString;
  onPress: () => void;
  active: boolean;
  disabled?: boolean;
  icon?: (props: IconProps) => Children;
  hasNotification?: boolean;
  rightAccessory?: Children;
};
function Tab({
  size,
  active,
  onPress,
  label,
  disabled,
  icon,
  hasNotification,
  rightAccessory,
}: TabProps) {
  const config = useBentoConfig().tabs;
  const { pressProps } = usePress({ onPress, isDisabled: disabled });

  return (
    <Box
      tabIndex={active || disabled ? -1 : 0}
      className={tabRecipe({ active, kind: config.kind })}
      {...pressProps}
      disabled={disabled}
      borderTopLeftRadius={
        config.kind === "folder"
          ? typeof config.radius === "object"
            ? config.radius.topLeft
            : config.radius
          : undefined
      }
      borderTopRightRadius={
        config.kind === "folder"
          ? typeof config.radius === "object"
            ? config.radius.topRight
            : config.radius
          : undefined
      }
      paddingX={config.paddingX[size]}
      paddingY={config.paddingY[size]}
      position="relative"
      borderColor={
        config.kind === "underline"
          ? active
            ? normalizeStatusValue(config.lineColor).active
            : config.lineColor
          : undefined
      }
      style={{
        borderBottomWidth: config.kind === "underline" ? config.lineHeight[size] : undefined,
      }}
      borderStyle="solid"
    >
      <Columns space={config.internalSpacing} alignY="center" align="center">
        {icon && (
          <Column width="content">{icon({ size: config.iconSize, color: "inherit" })}</Column>
        )}
        <Column width="content">
          <Label size={config.labelSize[size]} uppercase={config.uppercaseLabel}>
            {label}
          </Label>
        </Column>
        {rightAccessory}
        {hasNotification && (
          <Column width="content">
            <Box
              borderRadius="circled"
              color={config.notificationColor}
              background="currentColor"
              style={{
                width: config.notificationSize,
                height: config.notificationSize,
              }}
            ></Box>
          </Column>
        )}
      </Columns>
    </Box>
  );
}

export function Tabs<A>({ size, value, tabs, onChange }: Props<A>) {
  const config = useBentoConfig().tabs;
  return (
    <Box overflowX="auto">
      <Columns
        space={config.spaceBetweenTabs}
        align={config.tabsWidth === "fit-content" ? config.tabsAlignment : undefined}
      >
        {tabs.map((t) => (
          <Column key={t.label} width={config.tabsWidth === "fit-content" ? "content" : undefined}>
            <Tab
              size={size}
              label={t.label}
              onPress={() => onChange(t.value)}
              active={value === t.value}
              disabled={t.disabled}
              icon={t.icon}
              hasNotification={t.hasNotification}
              rightAccessory={t.rightAccessory}
            />
          </Column>
        ))}
      </Columns>
    </Box>
  );
}

export type { Props as TabsProps };
